import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  EditIcon,
  ExclamationTriangleIcon,
  SignOutIcon,
  HomeIcon,
  CreditCardIcon,
  CalendarIcon
} from '../utils/IconWrapper';
import { useAuth } from '../utils/AuthContext';
import { getUserBookings, BookingWithAccommodation, cancelBooking, getUserServiceRequests, ServiceRequest } from '../utils/supabaseExtended';
import { formatPrice } from '../utils/config';
import Layout from '../components/layout/Layout';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
`;

const DashboardHeader = styled.header`
  background: linear-gradient(135deg, #004744 0%, #004744 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
`;

const WelcomeTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const CardTitle = styled.h2`
  color: #004744;
  margin: 0;
  font-size: 1.25rem;
`;

const CardIcon = styled.div`
  color: #004744;
  font-size: 1.25rem;
`;

const ProfileStatus = styled.div<{ verified?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: ${props => props.verified ? 'rgba(0, 71, 68, 0.12)' : '#fef3c7'};
  border: 1px solid ${props => props.verified ? '#004744' : '#f59e0b'};
  margin-bottom: 1rem;
`;

const StatusIcon = styled.div<{ verified?: boolean }>`
  color: ${props => props.verified ? '#004744' : '#f59e0b'};
`;

const StatusText = styled.span<{ verified?: boolean }>`
  color: ${props => props.verified ? '#004744' : '#92400e'};
  font-weight: 500;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #374151;
  font-weight: 600;
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #004744 0%, #004744 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 71, 68, 0.4);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ef4444;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
  }
`;

const BookingCard = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
`;

const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const BookingTitle = styled.h3`
  color: #004744;
  margin: 0;
`;

const BookingStatus = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case 'confirmed':
        return 'background: rgba(0, 71, 68, 0.12); color: #004744; border: 1px solid #004744;';
      case 'pending':
        return 'background: #fef3c7; color: #92400e; border: 1px solid #f59e0b;';
      case 'cancelled':
        return 'background: #fef2f2; color: #991b1b; border: 1px solid #ef4444;';
      default:
        return 'background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;';
    }
  }}
`;

const BookingDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const Dashboard: React.FC = () => {
  const { user, profile, loading, logout } = useAuth();
  const [bookings, setBookings] = useState<BookingWithAccommodation[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingServices, setLoadingServices] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/register');
      return;
    }

    const fetchBookings = async () => {
      try {
        const userBookings = await getUserBookings();
        setBookings(userBookings);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoadingBookings(false);
      }
    };

    const fetchServiceRequests = async () => {
      try {
        const userServiceRequests = await getUserServiceRequests();
        setServiceRequests(userServiceRequests);
      } catch (error) {
        console.error('Error fetching service requests:', error);
      } finally {
        setLoadingServices(false);
      }
    };

    if (user) {
      fetchBookings();
      fetchServiceRequests();
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCancelBooking = (bookingId: string) => {
    setBookingToCancel(bookingId);
    setShowCancelModal(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;

    setCancellingBooking(true);
    try {
      const result = await cancelBooking(bookingToCancel);
      
      if (result.success) {
        // Ricarica le prenotazioni per aggiornare la lista
        const userBookings = await getUserBookings();
        setBookings(userBookings);
        alert('Prenotazione cancellata con successo!');
      } else {
        alert(result.error || 'Errore durante la cancellazione');
      }
    } catch (error) {
      alert('Errore durante la cancellazione della prenotazione');
    } finally {
      setCancellingBooking(false);
      setShowCancelModal(false);
      setBookingToCancel(null);
    }
  };

  const cancelCancelBooking = () => {
    setShowCancelModal(false);
    setBookingToCancel(null);
  };

  if (loading) {
    return <Layout><div>Caricamento...</div></Layout>;
  }

  if (!user) {
    return null;
  }

  const getVerificationStatus = () => {
    if (!profile) return { verified: false, message: 'Profilo in caricamento...' };
    
    const emailVerified = profile.email_verified;
    const phoneVerified = profile.phone_verified;
    const identityVerified = profile.identity_verified;
    
    if (emailVerified && phoneVerified && identityVerified) {
      return { verified: true, message: 'Account completamente verificato' };
    }
    
    const pending = [];
    if (!emailVerified) pending.push('email');
    if (!phoneVerified) pending.push('telefono');
    if (!identityVerified) pending.push('identità');
    
    return { 
      verified: false, 
      message: `Verifiche mancanti: ${pending.join(', ')}` 
    };
  };

  const verificationStatus = getVerificationStatus();

  return (
    <Layout>
      <DashboardContainer>
        <DashboardHeader>
          <WelcomeTitle>
            Ciao {profile?.full_name || user.email?.split('@')[0] || 'Utente'}! 👋
          </WelcomeTitle>
          <WelcomeSubtitle>
            Benvenuto nella tua dashboard J-O-H. Gestisci il tuo profilo e le tue prenotazioni.
          </WelcomeSubtitle>
        </DashboardHeader>

        <DashboardGrid>
          <Sidebar>
            {/* Profilo Card */}
            <Card>
              <CardHeader>
                <CardIcon><UserIcon /></CardIcon>
                <CardTitle>Il Tuo Profilo</CardTitle>
              </CardHeader>
              
              <ProfileStatus verified={verificationStatus.verified}>
                <StatusIcon verified={verificationStatus.verified}>
                  {verificationStatus.verified ? <CheckCircleIcon /> : <ClockIcon />}
                </StatusIcon>
                <StatusText verified={verificationStatus.verified}>
                  {verificationStatus.message}
                </StatusText>
              </ProfileStatus>

              <InfoRow>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoRow>
              
              {profile?.phone_number && (
                <InfoRow>
                  <InfoLabel>Telefono</InfoLabel>
                  <InfoValue>{profile.phone_number}</InfoValue>
                </InfoRow>
              )}
              
              {profile?.nationality && (
                <InfoRow>
                  <InfoLabel>Nazionalità</InfoLabel>
                  <InfoValue>{profile.nationality}</InfoValue>
                </InfoRow>
              )}

              <InfoRow>
                <InfoLabel>Fase Registrazione</InfoLabel>
                <InfoValue>{profile?.registration_phase || 1}/3</InfoValue>
              </InfoRow>

              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <ActionButton to="/profile/edit">
                  <EditIcon />
                  Modifica Profilo
                </ActionButton>
                
                {!verificationStatus.verified && (
                  <ActionButton to="/profile/verify">
                    <ExclamationTriangleIcon />
                    Completa Verifiche
                  </ActionButton>
                )}
                
                <LogoutButton onClick={handleLogout}>
                  <SignOutIcon />
                  Logout
                </LogoutButton>
              </div>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardIcon><HomeIcon /></CardIcon>
                <CardTitle>Azioni Rapide</CardTitle>
              </CardHeader>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <ActionButton to="/accommodations">
                  <HomeIcon />
                  Prenota Alloggio
                </ActionButton>
                
                <ActionButton to="/services">
                  <CreditCardIcon />
                  Esplora Servizi
                </ActionButton>
              </div>
            </Card>
          </Sidebar>

          <MainContent>
            {/* Prenotazioni */}
            <Card>
              <CardHeader>
                <CardIcon><CalendarIcon /></CardIcon>
                <CardTitle>Le Tue Prenotazioni</CardTitle>
              </CardHeader>

              {loadingBookings ? (
                <LoadingContainer>Caricamento prenotazioni...</LoadingContainer>
              ) : bookings.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><CalendarIcon /></EmptyIcon>
                  <h3>Nessuna prenotazione ancora</h3>
                  <p>Esplora i nostri alloggi e prenota il tuo soggiorno perfetto!</p>
                  <ActionButton to="/accommodations" style={{ display: 'inline-flex', marginTop: '1rem' }}>
                    <HomeIcon />
                    Esplora Alloggi
                  </ActionButton>
                </EmptyState>
              ) : (
                bookings.map((booking) => (
                  <BookingCard key={booking.id}>
                    <BookingHeader>
                      <div>
                        <BookingTitle>
                          Prenotazione #{booking.id.slice(0, 8)}
                        </BookingTitle>
                        <p>{booking.accommodations?.title_it}</p>
                      </div>
                      <BookingStatus status={booking.status}>
                        {booking.status === 'confirmed' && 'Confermata'}
                        {booking.status === 'pending' && 'In Attesa'}
                        {booking.status === 'cancelled' && 'Annullata'}
                        {booking.status === 'completed' && 'Completata'}
                      </BookingStatus>
                    </BookingHeader>
                    
                    <BookingDetails>
                      <InfoRow>
                        <InfoLabel>Check-in</InfoLabel>
                        <InfoValue>{new Date(booking.check_in).toLocaleDateString('it-IT')}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Check-out</InfoLabel>
                        <InfoValue>{new Date(booking.check_out).toLocaleDateString('it-IT')}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Ospiti</InfoLabel>
                        <InfoValue>{booking.guests_count}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Totale</InfoLabel>
                        <InfoValue>{formatPrice(booking.total_price)}</InfoValue>
                      </InfoRow>
                    </BookingDetails>
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                      {/* Dettagli prenotazione disattivati nel flusso Airbnb-only */}
                      {booking.status === 'pending' || booking.status === 'confirmed' ? (
                        <LogoutButton onClick={() => handleCancelBooking(booking.id)}>
                          Cancella
                        </LogoutButton>
                      ) : null}
                    </div>
                  </BookingCard>
                ))
              )}
            </Card>

            {/* Richieste Servizi */}
            <Card>
              <CardHeader>
                <CardIcon><CreditCardIcon /></CardIcon>
                <CardTitle>Le Tue Richieste Servizi</CardTitle>
              </CardHeader>

              {loadingServices ? (
                <LoadingContainer>Caricamento richieste servizi...</LoadingContainer>
              ) : serviceRequests.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><CreditCardIcon /></EmptyIcon>
                  <h3>Nessuna richiesta servizio ancora</h3>
                  <p>Esplora i nostri servizi e prenota esperienze uniche!</p>
                  <ActionButton to="/services/catalog" style={{ display: 'inline-flex', marginTop: '1rem' }}>
                    <CreditCardIcon />
                    Esplora Servizi
                  </ActionButton>
                </EmptyState>
              ) : (
                serviceRequests.map((request: any) => (
                  <BookingCard key={request.id}>
                    <BookingHeader>
                      <div>
                        <BookingTitle>
                          Richiesta #{request.id.slice(0, 8)}
                        </BookingTitle>
                        <p>{request.services?.title_it || 'Servizio non disponibile'}</p>
                      </div>
                      <BookingStatus status={request.status}>
                        {request.status === 'pending' && 'In Attesa'}
                        {request.status === 'contacted' && 'Contattato'}
                        {request.status === 'confirmed' && 'Confermata'}
                        {request.status === 'cancelled' && 'Annullata'}
                        {request.status === 'completed' && 'Completata'}
                      </BookingStatus>
                    </BookingHeader>
                    
                    <BookingDetails>
                      {request.requested_date && (
                        <InfoRow>
                          <InfoLabel>Data Richiesta</InfoLabel>
                          <InfoValue>{new Date(request.requested_date).toLocaleDateString('it-IT')}</InfoValue>
                        </InfoRow>
                      )}
                      
                      {request.requested_time && (
                        <InfoRow>
                          <InfoLabel>Orario Richiesto</InfoLabel>
                          <InfoValue>{request.requested_time}</InfoValue>
                        </InfoRow>
                      )}
                      
                      <InfoRow>
                        <InfoLabel>Partecipanti</InfoLabel>
                        <InfoValue>{request.participants_count}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Categoria</InfoLabel>
                        <InfoValue>{request.services?.category || 'N/A'}</InfoValue>
                      </InfoRow>
                    </BookingDetails>
                    
                    {request.message && (
                      <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                        <strong>Messaggio:</strong>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280' }}>{request.message}</p>
                      </div>
                    )}
                  </BookingCard>
                ))
              )}
            </Card>
          </MainContent>
        </DashboardGrid>
        
        {/* Modale di conferma cancellazione */}
        {showCancelModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#dc2626', marginBottom: '1rem' }}>
                ⚠️ Conferma Cancellazione
              </h3>
              <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
                Sei sicuro di voler cancellare questa prenotazione?
                <br />
                <strong>Questa azione non può essere annullata.</strong>
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <ActionButton 
                  as="button"
                  onClick={cancelCancelBooking}
                  style={{ backgroundColor: '#6b7280' }}
                >
                  Annulla
                </ActionButton>
                <LogoutButton 
                  onClick={confirmCancelBooking}
                  disabled={cancellingBooking}
                >
                  {cancellingBooking ? 'Cancellazione...' : 'Conferma Cancellazione'}
                </LogoutButton>
              </div>
            </div>
          </div>
        )}
      </DashboardContainer>
    </Layout>
  );
};

export default Dashboard; 